import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams, Navigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { 
  HardHat, 
  Filter,
  Search,
  CheckCircle2,
  Loader2,
  Shield,
  Calculator,
  FileText
} from 'lucide-react'
import { SolarNewsTicker } from '../components/SolarNewsTicker'
import VendorCard from '../components/enterprise/VendorCard'
import { CreateRfqModal } from '../components/enterprise/CreateRfqModal'
import apiService from '../services/api'
import { useAuthStore } from '../store/authStore'
import { getUserType, shouldShowFeature } from '../utils/userPermissions'

interface Vendor {
  id: string
  companyName: string
  locationCity: string
  locationState: string
  avatar: string | null
  completedJobs: number
  memberSince: string
  // Government vendor fields
  isGovCertified?: boolean
  cageCode?: string
  dunsNumber?: string
  gsaScheduleId?: string
  certifications?: string[]
  approvedAgencies?: string[]
  govContractsCompleted?: number
}

interface PaginationInfo {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export default function Enterprise() {
  const { user } = useAuthStore()
  const userType = getUserType(user)
  
  // Role-based access control
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  // REFACTORED: Standardized Enterprise Buyer Check
  // Vendor directory is exclusively for Enterprise Buyers (role=BUYER + isEnterprise=true)
  // Note: getUserType() now uses standardized logic internally
  if (userType !== 'ENTERPRISE_BUYER') {
    return <Navigate to="/" replace />
  }

  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([])
  const [allVendors, setAllVendors] = useState<Vendor[]>([]) // Store all loaded vendors
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [govCertifiedOnly, setGovCertifiedOnly] = useState(false)
  const [searchParams] = useSearchParams()
  const [showQuoteSuccess, setShowQuoteSuccess] = useState(false)
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    limit: 12,
    offset: 0,
    hasMore: true
  })
  
  // Modal state for Request Quote
  const [isRfqModalOpen, setIsRfqModalOpen] = useState(false)

  // Check if user was redirected from ROI calculator
  useEffect(() => {
    if (searchParams.get('quote_requested') === 'true') {
      setShowQuoteSuccess(true)
      // Hide the message after 5 seconds
      setTimeout(() => setShowQuoteSuccess(false), 5000)
    }
  }, [searchParams])

  useEffect(() => {
    fetchVendors(true) // Initial load
  }, [govCertifiedOnly])

  useEffect(() => {
    filterVendors()
  }, [allVendors, searchTerm, cityFilter, stateFilter])

  // Infinite scroll detection
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop
        >= document.documentElement.offsetHeight - 1000 && // Load when 1000px from bottom
        pagination?.hasMore &&
        !loading &&
        !loadingMore &&
        !searchTerm &&
        !cityFilter &&
        !stateFilter &&
        !govCertifiedOnly // Only auto-load when no filters are active
      ) {
        fetchVendors(false) // Load more
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [pagination?.hasMore, loading, loadingMore, searchTerm, cityFilter, stateFilter, govCertifiedOnly])

  const fetchVendors = async (isInitialLoad: boolean = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true)
        setAllVendors([])
        setPagination(prev => ({ ...prev, offset: 0 }))
      } else {
        setLoadingMore(true)
      }

      const offset = isInitialLoad ? 0 : (pagination?.offset || 0) + (pagination?.limit || 12)
      const limit = pagination?.limit || 12
      const govParam = govCertifiedOnly ? '&govCertifiedOnly=true' : ''
      const response = await apiService.get(`/api/enterprise/vendors?limit=${limit}&offset=${offset}${govParam}`) as any
      
      if (response?.success && response?.data) {
        const newVendors = response.data || []
        const newPagination = response.pagination || {
          total: 0,
          limit: limit,
          offset: offset,
          hasMore: false
        }

        if (isInitialLoad) {
          setAllVendors(newVendors)
        } else {
          setAllVendors(prev => [...prev, ...newVendors])
        }
        
        setPagination(newPagination)
      } else {
        setError('Failed to fetch vendors')
      }
    } catch (err) {
      setError('Failed to fetch vendors')
      console.error('Error fetching vendors:', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const filterVendors = useCallback(() => {
    let filtered = allVendors.filter(vendor => vendor.companyName) // Only show vendors with company names

    if (searchTerm) {
      filtered = filtered.filter(vendor =>
        vendor.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.locationCity?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.locationState?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (cityFilter) {
      filtered = filtered.filter(vendor =>
        vendor.locationCity?.toLowerCase().includes(cityFilter.toLowerCase())
      )
    }

    if (stateFilter) {
      filtered = filtered.filter(vendor =>
        vendor.locationState?.toLowerCase().includes(stateFilter.toLowerCase())
      )
    }

    setFilteredVendors(filtered)
  }, [allVendors, searchTerm, cityFilter, stateFilter])

  const getUniqueStates = () => {
    const states = allVendors
      .filter(vendor => vendor.locationState)
      .map(vendor => vendor.locationState)
    return [...new Set(states)].sort()
  }

  const getUniqueCities = () => {
    const cities = allVendors
      .filter(vendor => vendor.locationCity)
      .map(vendor => vendor.locationCity)
    return [...new Set(cities)].sort()
  }

  const handleLoadMore = () => {
    if (pagination?.hasMore && !loadingMore) {
      fetchVendors(false)
    }
  }

  // Modal handlers
  const handleRequestQuote = (_vendorId: string) => {
    if (shouldShowFeature(user, 'canRequestQuote')) {
      setIsRfqModalOpen(true)
    } else {
      toast.error('Account approval required to request quotes')
    }
  }

  const handleCloseRfqModal = () => {
    setIsRfqModalOpen(false)
  }

  const handleRfqSuccess = () => {
    setIsRfqModalOpen(false)
    toast.success('Quote request submitted successfully!')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading enterprise vendors...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => fetchVendors(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center space-x-3 mb-4">
            <HardHat className="h-8 w-8 text-primary-600" />
            <h1 className="text-3xl font-bold text-gray-900">Enterprise Directory</h1>
          </div>
          <p className="text-gray-600 max-w-2xl">
            Connect with verified solar enterprise vendors for large-scale commercial and industrial jobs. 
            Browse our directory of trusted partners with proven track records.
          </p>
          
          {/* Navigation Tabs */}
          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between">
              <nav className="flex space-x-8">
                <div className="flex items-center space-x-2 pb-2 border-b-2 border-primary-600">
                  <HardHat className="h-5 w-5 text-primary-600" />
                  <span className="font-medium text-primary-600">Vendor Directory</span>
                </div>
                {/* Only show Government RFQs tab for Enterprise Vendors */}
                {((user?.role === 'VENDOR' && user?.isEnterprise) || user?.role === 'ENTERPRISE_VENDOR') && (
                  <Link 
                    to="/enterprise/gov-rfqs" 
                    className="flex items-center space-x-2 pb-2 border-b-2 border-transparent hover:border-emerald-600 text-gray-600 hover:text-emerald-600 transition-colors"
                  >
                    <Shield className="h-5 w-5" />
                    <span className="font-medium">Government RFQs</span>
                  </Link>
                )}
              </nav>
              
              {/* Action buttons for Enterprise Buyers when no second tab */}
              {!((user?.role === 'VENDOR' && user?.isEnterprise) || user?.role === 'ENTERPRISE_VENDOR') && (
                <div className="flex items-center space-x-3">
                  <Link
                    to="/roi-calculator"
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Calculator className="h-4 w-4 mr-2" />
                    ROI Calculator
                  </Link>
                  <Link
                    to="/enterprise/rfqs"
                    className="inline-flex items-center px-4 py-2 border border-primary-600 text-primary-600 text-sm font-medium rounded-lg hover:bg-primary-50 transition-colors"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    My RFQs
                  </Link>

                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quote Request Success Message */}
      {showQuoteSuccess && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mx-4 sm:mx-6 lg:mx-8 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <CheckCircle2 className="h-5 w-5 text-green-400 mr-3" />
            <div>
              <p className="text-sm text-green-700">
                <strong>Quote request submitted successfully!</strong> Our enterprise vendors will review your project details and contact you with competitive quotes.
              </p>
            </div>
            <button
              onClick={() => setShowQuoteSuccess(false)}
              className="ml-auto text-green-400 hover:text-green-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Solar News Ticker */}
      <SolarNewsTicker />

      {/* Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vendors, cities, or states..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter className="h-5 w-5" />
              <span>Filters</span>
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              {/* Government Certification Toggle */}
              <div className="mb-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={govCertifiedOnly}
                    onChange={(e) => setGovCertifiedOnly(e.target.checked)}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <div className="flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-emerald-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Show government-certified vendors only
                    </span>
                  </div>
                </label>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <select
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Cities</option>
                    {getUniqueCities().map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All States</option>
                    {getUniqueStates().map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            {filteredVendors.length} enterprise vendor{filteredVendors.length !== 1 ? 's' : ''} found
                      {(pagination?.total || 0) > 0 && (searchTerm || cityFilter || stateFilter || govCertifiedOnly) && (
            <span className="text-gray-500"> (filtered from {pagination?.total || 0} total)</span>
          )}
          </p>
        </div>

        {/* Vendor Grid */}
        {filteredVendors.length === 0 ? (
          <div className="text-center py-12">
            <HardHat className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVendors.map((vendor) => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  onViewDetails={(vendorId) => {
                    window.location.href = `/enterprise/vendor/${vendorId}`
                  }}
                  onRequestQuote={handleRequestQuote}
                />
              ))}
            </div>

            {/* Load More / Loading State */}
            {(searchTerm || cityFilter || stateFilter || govCertifiedOnly) ? (
              // Show manual load more button when filters are active
              pagination?.hasMore && (
                <div className="text-center mt-8">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mx-auto"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Loading more vendors...</span>
                      </>
                    ) : (
                      <span>Load More Vendors</span>
                    )}
                  </button>
                </div>
              )
            ) : (
              // Show loading indicator for infinite scroll
              loadingMore && (
                <div className="text-center mt-8">
                  <div className="flex items-center justify-center space-x-2 text-gray-600">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Loading more vendors...</span>
                  </div>
                </div>
              )
            )}

            {/* End of results indicator */}
            {!pagination?.hasMore && allVendors.length > 0 && (
              <div className="text-center mt-8 py-4">
                <p className="text-gray-500">You've seen all available vendors</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Request Quote Modal */}
      <CreateRfqModal
        isOpen={isRfqModalOpen}
        onClose={handleCloseRfqModal}
        onSuccess={handleRfqSuccess}
      />
    </div>
  )
} 